/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LogInButton from 'flarum/forum/components/LogInButton';
import extractText from 'flarum/common/utils/extractText';
import type Mithril from 'mithril';
import type Model from 'flarum/common/Model';
import type User from 'flarum/common/models/User';

/**
 * A linked account record as served by fof/oauth's `linked-accounts` resource.
 */
export interface LinkedAccountProvider extends Model {
	name(): string;
	icon(): string;
	linked(): boolean;
	orphaned(): boolean;
}

declare module 'flarum/forum/ForumApplication' {
	export default interface ForumApplication {
		/** Set by fof/oauth while an account-linking popup is open. */
		fof_oauth_linkingInProgress?: boolean;
		/** The provider name currently being linked by fof/oauth. */
		fof_oauth_linkingProvider?: string;
	}
}

export interface LinkedAccountsAttrs extends ComponentAttrs {
	user: User;
	hideManage?: boolean;
	onSuccess?: () => void;
}

export default class LinkedAccounts extends Component<LinkedAccountsAttrs> {
	loading!: boolean;
	provider?: LinkedAccountProvider;

	oninit(vnode: Mithril.Vnode<LinkedAccountsAttrs, this>) {
		super.oninit(vnode);
		this.loading = true;
	}

	oncreate(vnode: Mithril.VnodeDOM<LinkedAccountsAttrs, this>) {
		super.oncreate(vnode);
		this.loadLinkedAccounts();
	}

	onbeforeupdate(vnode: Mithril.VnodeDOM<LinkedAccountsAttrs, this>) {
		super.onbeforeupdate(vnode);

		this.provider = app.store.all<LinkedAccountProvider>('linked-accounts').find((linkedAccount) => {
			return linkedAccount.name() === 'oft';
		});

		if (app.fof_oauth_linkingInProgress && app.fof_oauth_linkingProvider === this.provider?.name()) {
			this.loading = true;
		} else if (app.fof_oauth_linkingInProgress === false && app.fof_oauth_linkingProvider === this.provider?.name()) {
			this.loading = false;
			delete app.fof_oauth_linkingInProgress;
			delete app.fof_oauth_linkingProvider;

			this.attrs.onSuccess?.();
		}
	}

	view() {
		const { provider } = this;
		const { user, hideManage } = this.attrs;

		if (!provider) return null;

		const className = `Button FoFLogInButton LogInButton--${provider.name()} LogInButton${provider.linked() ? '--linked' : '--unlinked'}`;
		const brandName = app.forum.attribute('orellthalia_brandName');
		const communityName = app.forum.attribute('orellthalia_communityName');

		return (
			<div className="LinkedAccounts">
				<fieldset>
					{!hideManage && (
						<>
							<legend>{app.translator.trans('fof-oauth.forum.user.settings.linked-account.label', { brandName, communityName })}</legend>
							<p className="helpText">{app.translator.trans('fof-oauth.forum.user.settings.linked-account.help', { brandName, communityName })}</p>
							{provider.linked() && (
								<Button className={className} icon={provider.icon()} onclick={() => this.deleteProvider(provider)} loading={this.loading}>
									{app.translator.trans('fof-oauth.forum.unlink')}
								</Button>
							)}
						</>
					)}

					{((!provider.linked() && !provider.orphaned()) || hideManage) && (
						<div
							onclick={() => {
								app.fof_oauth_linkingProvider = provider.name();
								app.fof_oauth_linkingInProgress = true;
								this.loading = true;
								m.redraw();
							}}
						>
							<LogInButton className={className} icon={provider.icon()} path={`/auth/${provider.name()}?linkTo=${user.id()}`} loading={this.loading}>
								{app.translator.trans(`fof-oauth.forum.log_in.with_${provider.name()}_button`, {
									provider: app.translator.trans(`fof-oauth.forum.providers.${provider.name()}`),
								})}
							</LogInButton>
						</div>
					)}
				</fieldset>
			</div>
		);
	}

	async loadLinkedAccounts() {
		const linkedAccounts = await app.store.find<LinkedAccountProvider[]>('linked-accounts');

		this.provider = linkedAccounts.find((linkedAccount) => linkedAccount.name() === 'oft');

		this.loading = false;
		m.redraw();
	}

	async deleteProvider(provider: LinkedAccountProvider) {
		if (
			confirm(
				extractText(
					app.translator.trans('fof-oauth.forum.user.settings.linked-account.unlink-confirm', {
						provider: app.translator.trans(`fof-oauth.forum.providers.${provider.name()}`),
					})
				)
			)
		) {
			this.loading = true;
			await provider.delete();
			await app.store.find<LinkedAccountProvider[]>('linked-accounts', {});
			this.loading = false;
			m.redraw();
		}
	}
}
